import axios from 'axios'

const getUrl = () => {
  return process.env.REACT_APP_STAGE === 'production' ?
    `https://lobbyapi.parso.cr`:
    `https://apidev.parso.cr`
}

const getBaseUrl = () => `${getUrl()}/api`;

const
  api = axios.create()

api.interceptors.request.use(
  config => {
    config.headers['tenant'] = localStorage.getItem('domain') || 'public'

    let sessionTokens = localStorage.getItem('tokens') || '{}'
    sessionTokens = JSON.parse(sessionTokens)
    if (sessionTokens['access-token']) {
      config.headers['access-token'] = sessionTokens['access-token']
      config.headers['client'] = sessionTokens['client']
      config.headers['uid'] = sessionTokens['uid']
    }
    config.baseURL = config.baseURL || getBaseUrl()
    if (!config.skipClientType) {
      config.params = {
        ...config.params,
        client_type: 'WEB'
      }
    }
    return config
  }
)

const doneHandler = response => {
  if (response?.headers['access-token']) {
    setTokens(response.headers)
  }
  return response
}

const errorHandler = error => {
  return Promise.reject(error)
}

api.interceptors.response.use(doneHandler, errorHandler)


// Los cinco headers que `devise_token_auth` emite y que el interceptor de
// request necesita reenviar. Se filtran en lugar de guardar `response.headers`
// completo: así un header suelto no puede pisar al `client` bueno con
// `undefined`, y `localStorage.tokens` no arrastra decenas de entradas ajenas.
const DTA_HEADERS = ['access-token', 'client', 'uid', 'expiry', 'token-type']

const setTokens = headers => {
  const tokens = DTA_HEADERS.reduce((acc, name) => {
    if (headers[name] != null) acc[name] = headers[name]
    return acc
  }, {})
  localStorage.setItem('tokens', JSON.stringify(tokens))
}

/**
 * Vencimiento de la sesión actual en milisegundos (epoch), o `null` si no hay
 * sesión o el header no es utilizable.
 *
 * El backend fija `expiry` en el momento del login y NO lo mueve
 * (`change_headers_on_each_request = false`), con un lifespan de 14 días que
 * para las cuentas de recepción es una política fija — no se hereda la del
 * tenant, porque un tenant con 15 minutos de vigencia dejaría el kiosko
 * inoperable. Es decir: el vencimiento llega igual, se use el kiosko o no.
 *
 * Exponerlo permite avisar antes de que caiga el 401, en vez de que la sesión
 * muera en medio de registrar a un visitante.
 */
const getSessionExpiry = () => {
  try {
    const { expiry } = JSON.parse(localStorage.getItem('tokens') || '{}')
    const seconds = Number(expiry)
    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : null
  } catch {
    return null
  }
}

const clearAuth = () => {
  localStorage.setItem('tokens', '{}')
  localStorage.setItem('domain', 'public')
}

/**
 * Single, cheap round-trip to confirm whether the current session is still
 * valid before committing to a destructive logout (`clearLocalDatabase()`
 * wipes Dexie). Used to rule out a transient 401 (proxy hiccup, momentary
 * backend issue, etc.) instead of blindly trusting the very first 401 a
 * caller sees — a simplified alternative to CorporateExperienceWebapp's
 * token-stack retry mechanism, without maintaining a history of tokens.
 *
 * Solo un **401 explícito** prueba que el servidor rechazó estos tokens.
 * Cualquier otro fallo (500, 502, timeout, kiosko sin red) no dice nada sobre
 * la sesión, así que se concede el beneficio de la duda: destruir la sesión
 * exigiría evidencia, y un error de servidor no lo es.
 *
 * Esto no es hipotético: CorporateService monta la ruta
 * `token_validations: 'api/users/token_validations'` pero el controlador no
 * existe, así que hoy este endpoint responde **500**. Colapsar cualquier error
 * en "sesión inválida" dejaba la salvaguarda permanentemente en corto y
 * convertía cada 401 transitorio en un logout.
 *
 * @returns {Promise<boolean>} `false` solo si el backend rechaza los tokens
 *   con un 401; `true` en cualquier otro caso.
 */
const isSessionValid = async () => {
  try {
    await api.get('/auth/validate_token', { baseURL: getUrl() })
    return true
  } catch (error) {
    return error?.response?.status !== 401
  }
}

export {
  api,
  getUrl,
  getBaseUrl,
  setTokens,
  clearAuth,
  isSessionValid,
  getSessionExpiry
}
